(ns minorodata.components.warehouse-usage.account-usage
  (:require
   ["date-fns" :refer [format parseISO]]
   ["lucide-react" :refer [Snowflake]]
   [minorodata.components.home-dashboard.shared :refer [chart-colors format-duration stat-card]]
   [minorodata.components.home-dashboard.toolbar :as toolbar]
   [minorodata.components.warehouse-usage.query-diagnosis :as query-diagnosis]
   [minorodata.components.warehouse-usage.shared :refer [bucket-date-key bucketed-series
                                                         chart-card chart-tick-class
                                                         dimension-label ellipsize
                                                         empty-state format-bytes
                                                         format-integer format-number state-card
                                                         timeframe]]
   [minorodata.components.spinner.core :as spinner]
   [re-frame.core :as re-frame]
   [reagent.core :as reagent]
   [recharts :refer [Bar BarChart CartesianGrid Tooltip XAxis YAxis]]
   [ui.fields :as fields]
   [ui.table :as table]
   [ui.typography :as typography]))

(defn- iso-date
  [date]
  (format date "yyyy-MM-dd"))

(defn- request-account-usage! [{:keys [from to]}]
  (re-frame/dispatch [:usage-account.events/init
                      {:from (iso-date from)
                       :to   (iso-date to)}]))

(defn refresh!
  []
  (request-account-usage! @timeframe)
  (query-diagnosis/refresh!))

(defn date-selector []
  [toolbar/date-range-dropdown {:timeframe @timeframe
                                :on-change (fn [new-timeframe]
                                             (reset! timeframe new-timeframe)
                                             (request-account-usage! new-timeframe)
                                             (query-diagnosis/refresh!))}])

(defn- trend-of
  [current previous]
  (when (and (some? current) (some? previous))
    (cond
      (< current previous) :down
      (> current previous) :up
      :else                 :neutral)))

(defn- ms->seconds [ms]
  (some-> ms (/ 1000)))

(defn format-avg-runtime
  [seconds]
  (cond
    (nil? seconds) "—"
    (< seconds 1)  (str (.toFixed seconds 1) "s")
    :else          (format-duration seconds)))

(defn- kpi-cards [totals prev loading?]
  [:div {:class "grid grid-cols-1 gap-6 md:grid-cols-2 xl:grid-cols-4"}
   [stat-card {:title      "Total Queries"
               :value      (format-integer (:query-count totals))
               :prev-value (when prev (format-integer (:query-count prev)))
               :trend      (trend-of (:query-count totals) (:query-count prev))
               :loading    loading?
               :tooltip    "Number of queries executed in the selected period."}]
   [stat-card {:title      "Avg Runtime"
               :value      (format-avg-runtime (ms->seconds (:avg-elapsed-ms totals)))
               :prev-value (when (some? (:avg-elapsed-ms prev))
                             (format-avg-runtime (ms->seconds (:avg-elapsed-ms prev))))
               :trend      (trend-of (:avg-elapsed-ms totals) (:avg-elapsed-ms prev))
               :invert     true
               :loading    loading?
               :tooltip    "Average elapsed time per query in the selected period."}]
   [stat-card {:title      "Bytes Scanned"
               :value      (format-bytes (:bytes-scanned totals))
               :prev-value (when (some? (:bytes-scanned prev)) (format-bytes (:bytes-scanned prev)))
               :trend      (trend-of (:bytes-scanned totals) (:bytes-scanned prev))
               :loading    loading?
               :tooltip    "Total bytes scanned by queries in the selected period."}]
   [stat-card {:title      "Remote Storage Spill"
               :value      (format-bytes (:bytes-spilled totals))
               :prev-value (when (some? (:bytes-spilled prev)) (format-bytes (:bytes-spilled prev)))
               :trend      (trend-of (:bytes-spilled totals) (:bytes-spilled prev))
               :invert     true
               :loading    loading?
               :tooltip    "Bytes spilled to remote storage - a sign a warehouse is undersized for its workload."}]])

(def ^:private credit-category-order
  [[:active-credits            "Active"              (nth chart-colors 0)]
   [:compilation-credits       "Compilation"          (nth chart-colors 1)]
   [:queue-provisioning-credits "Queue Provisioning"  (nth chart-colors 2)]
   [:queue-overload-credits    "Queue Overload"       (nth chart-colors 3)]
   [:idle-credits              "Other Idle"           (nth chart-colors 4)]])

(defn- credits-breakdown-chart [rows loading?]
  [chart-card
   "Credits Breakdown"
   nil
   (cond
     loading?
     [:div {:class "flex h-[300px] w-full items-center justify-center"}
      [spinner/with-text {:text "Loading credits breakdown..."}]]

     (empty? rows)
     [empty-state Snowflake "No credits breakdown for this period yet."]

     :else
     [:div {:class "flex min-w-0 gap-6"}
      [:div {:class "min-w-0 flex-1"}
       [:> BarChart {:width "100%" :height 300 :data rows :margin #js {:top 20 :bottom 20}}
        [:> CartesianGrid {:vertical false}]
        [:> XAxis {:dataKey "bucket"
                   :axis-line false
                   :angle -30
                   :height 55
                   :text-anchor "end"
                   :tick {:class chart-tick-class}
                   :tick-formatter #(format (parseISO %) "d MMM")}]
        [:> YAxis {:type "number" :axis-line false :tick-line false :tick {:class chart-tick-class}}]
        [:> Tooltip {:label-formatter #(format (parseISO %) "d MMM yyyy")}]
        (doall
         (for [[k label color] credit-category-order]
           [:> Bar {:key (name k) :dataKey (name k) :name label :stackId "credits" :fill color}]))]]
      [:div {:class "w-44 shrink-0 self-center max-h-[300px] overflow-y-auto pr-1"}
       [typography/h4 {:class "mb-3"} "Credit Category"]
       [:div {:class "flex flex-col gap-2"}
        (doall
         (for [[k label color] credit-category-order]
           ^{:key k}
           [:div {:class "flex min-w-0 items-center gap-2"}
            [:span {:class "h-2.5 w-2.5 shrink-0 rounded-sm" :style {:background-color color}}]
            [:span {:class (str "min-w-0 truncate " chart-tick-class)} label]]))]]])])

(defn- execution-time-chart [rows loading?]
  (let [{:keys [rows series]} (bucketed-series rows :warehouse-name :execution-ms)]
    [chart-card
     "Execution Time"
     nil
     (cond
       loading?
       [:div {:class "flex h-[300px] w-full items-center justify-center"}
        [spinner/with-text {:text "Loading execution time..."}]]

       (empty? rows)
       [empty-state Snowflake "No execution time data for this period yet."]

       :else
       [:div {:class "flex min-w-0 gap-6"}
        [:div {:class "min-w-0 flex-1"}
         [:> BarChart {:width "100%" :height 300 :data rows :margin #js {:top 20 :bottom 20}}
          [:> CartesianGrid {:vertical false}]
          [:> XAxis {:dataKey bucket-date-key
                     :axis-line false
                     :angle -30
                     :height 55
                     :text-anchor "end"
                     :tick {:class chart-tick-class}
                     :tick-formatter #(format (parseISO %) "d MMM")}]
          [:> YAxis {:type "number" :axis-line false :tick-line false
                     :tick {:class chart-tick-class}
                     :tick-formatter #(format-avg-runtime (ms->seconds %))}]
          [:> Tooltip {:label-formatter #(format (parseISO %) "d MMM yyyy")
                       :formatter (fn [value] (format-avg-runtime (ms->seconds value)))}]
          (doall
           (for [[index warehouse] (map-indexed vector series)]
             [:> Bar {:key warehouse
                      :dataKey warehouse
                      :name (dimension-label warehouse)
                      :stackId "execution"
                      :fill (nth chart-colors index "var(--color-slate-500)")}]))]]
        [:div {:class "w-44 shrink-0 self-center max-h-[300px] overflow-y-auto pr-1"}
         [typography/h4 {:class "mb-3"} "Warehouse Name"]
         [:div {:class "flex flex-col gap-2"}
          (doall
           (for [[index warehouse] (map-indexed vector series)]
             ^{:key warehouse}
             [:div {:class "flex min-w-0 items-center gap-2"}
              [:span {:class "h-2.5 w-2.5 shrink-0 rounded-sm"
                      :style {:background-color (nth chart-colors index "var(--color-slate-500)")}}]
              [:span {:class (str "min-w-0 truncate " chart-tick-class)
                      :title (dimension-label warehouse)}
               (ellipsize (dimension-label warehouse) 22)]]))]]])]))

(defn- kpi-section [status totals prev]
  (case status
    :not-provisioned [state-card [empty-state Snowflake "Snowflake usage isn't available for your account."]]
    :error            [state-card [empty-state Snowflake "Failed to load account usage data."]]
    :loaded           [kpi-cards totals prev false]
    [kpi-cards totals prev true]))

(defn- credits-breakdown-section [status rows]
  (case status
    :not-provisioned [state-card [empty-state Snowflake "Snowflake usage isn't available for your account."]]
    :error            [state-card [empty-state Snowflake "Failed to load Credits Breakdown data."]]
    :loaded           [credits-breakdown-chart rows false]
    [credits-breakdown-chart rows true]))

(defn- execution-time-section [status rows]
  (case status
    :not-provisioned [state-card [empty-state Snowflake "Snowflake usage isn't available for your account."]]
    :error            [state-card [empty-state Snowflake "Failed to load Execution Time data."]]
    :loaded           [execution-time-chart rows false]
    [execution-time-chart rows true]))

(def ^:private weekday-order [1 2 3 4 5 6 0])

(def ^:private weekday-label
  {0 "Sunday" 1 "Monday" 2 "Tuesday" 3 "Wednesday" 4 "Thursday" 5 "Friday" 6 "Saturday"})

(def ^:private hours-of-day (vec (range 24)))

(def ^:private heat-scale
  [["bg-kleene-secondary-05 dark:bg-kleene-dark-02" "text-kleene-text"]
   ["bg-kleene-secondary-04 dark:bg-kleene-dark-03" "text-kleene-text"]
   ["bg-kleene-secondary-03 dark:bg-kleene-dark-04" "text-white"]
   ["bg-kleene-secondary-02 dark:bg-kleene-dark-05" "text-white"]
   ["bg-kleene-secondary-01 dark:bg-kleene-secondary-v2" "text-white"]])

(def ^:private no-data-classes ["bg-kleene-neutral-05 dark:bg-kleene-dark-01" "text-kleene-text-secondary"])

(defn- heat-classes
  [value max-value]
  (if (or (nil? value) (nil? max-value) (zero? max-value))
    no-data-classes
    (let [ratio (/ value max-value)
          idx   (-> (* ratio (count heat-scale)) int (min (dec (count heat-scale))) (max 0))]
      (nth heat-scale idx))))

(defn- load-profile-lookup
  [rows warehouse]
  (let [rows (if (empty? warehouse)
               rows
               (filter #(= warehouse (:warehouse-name %)) rows))]
    (reduce (fn [lookup {:keys [day-of-week hour-of-day credits-used]}]
              (update lookup [day-of-week hour-of-day] (fnil + 0) credits-used))
            {}
            rows)))

(defn- warehouse-options [rows]
  (into [{:value "" :text "All warehouses"}]
        (->> rows (keep :warehouse-name) distinct sort (map (fn [w] {:value w :text w})))))

(defonce ^:private selected-warehouse (reagent/atom ""))

(defn- warehouse-select [rows selected]
  [fields/select {:value      selected
                  :on-change  (fn [e] (reset! selected-warehouse (.. e -target -value)))
                  :options    (warehouse-options rows)
                  :full-width false}])

(defn- heatmap-table [lookup]
  (let [max-value (or (some->> lookup vals seq (apply max)) 0)]
    [:div {:class "overflow-x-auto"}
     [table/table
      [table/thead
       [table/tr
        [table/th "Day / Hour"]
        (for [h hours-of-day]
          ^{:key h} [table/th {:class "text-center"} h])]]
      [table/tbody
       (for [day weekday-order]
         ^{:key day}
         [table/tr
          [table/td {:class "whitespace-nowrap font-medium"} (weekday-label day)]
          (for [h hours-of-day]
            (let [value                 (get lookup [day h])
                  [bg-class text-class] (heat-classes value max-value)]
              ^{:key h}
              [table/td {:class (str "text-center text-xs " bg-class " " text-class)}
               (format-number value)]))])]]]))

(defn- heatmap-card [rows loading?]
  (let [valid-selection? (contains? (set (map :value (warehouse-options rows))) @selected-warehouse)
        selected         (if valid-selection? @selected-warehouse "")
        lookup           (when-not loading? (load-profile-lookup rows selected))]
    [chart-card
     "Warehouse load heatmap"
     (when-not loading? [warehouse-select rows selected])
     (cond
       loading?
       [:div {:class "flex h-48 w-full items-center justify-center"}
        [spinner/with-text {:text "Loading warehouse load heatmap..."}]]

       (empty? lookup)
       [empty-state Snowflake "No warehouse load data for the selected warehouse in this period."]

       :else
       [heatmap-table lookup])]))

(defn- heatmap-section [status rows]
  (case status
    :not-provisioned [state-card [empty-state Snowflake "Snowflake usage isn't available for your account."]]
    :error            [state-card [empty-state Snowflake "Failed to load Warehouse usage data."]]
    :loaded (if (empty? rows)
              [state-card [empty-state Snowflake "No warehouse load data for this period yet."]]
              [heatmap-card rows false])
    [heatmap-card nil true]))

(defn usage-tab []
  (reagent/create-class
   {:display-name
    "usage-account-tab"

    :constructor
    #(do (request-account-usage! @timeframe)
         (query-diagnosis/refresh!))

    :render
    (fn []
      (let [query-cost-status        @(re-frame/subscribe [:usage-account.subs/query-cost-status])
            query-cost                @(re-frame/subscribe [:usage-account.subs/query-cost])
            previous-totals-status    @(re-frame/subscribe [:usage-account.subs/previous-totals-status])
            previous-totals           @(re-frame/subscribe [:usage-account.subs/previous-totals])
            warehouse-runtime-status  @(re-frame/subscribe [:usage-account.subs/warehouse-runtime-status])
            warehouse-runtime         @(re-frame/subscribe [:usage-account.subs/warehouse-runtime])
            heatmap-status            @(re-frame/subscribe [:usage-account.subs/heatmap-status])
            load-profile              @(re-frame/subscribe [:usage-account.subs/load-profile])]
        [:div {:class "flex flex-col gap-6 pt-4"}
         [kpi-section query-cost-status (:totals query-cost)
          (when (= previous-totals-status :loaded) previous-totals)]
         [:div {:class "grid grid-cols-1 gap-6 xl:grid-cols-2"}
          [credits-breakdown-section query-cost-status (:rows query-cost)]
          [execution-time-section warehouse-runtime-status warehouse-runtime]]
         [query-diagnosis/section]
         [heatmap-section heatmap-status load-profile]]))}))
